import { useDisclosure } from "@chakra-ui/react";
import { Spinner } from "@components/ui-kit";
import { NotFoundProject } from "@components/widgets/ErrorPage";
import { Trans, t } from "@lingui/macro";
import {
  useGetProjectEditDataQuery,
  useUpdateProjectMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { withCan } from "@src/utils/components/permissions";
import { getQueryParam, getQueryParamOptional } from "@src/utils/routing";
import { omit } from "lodash";
import { observer } from "mobx-react-lite";
import Router, { useRouter } from "next/router";
import React, { type FormEvent, useContext, useState } from "react";
import { FormProjectCmp } from "../common/project-form/FormProjectCmp";
import {
  FormProjectCtx,
  StoreProjectForm,
} from "../common/project-form/StoreProjectForm";

const FormEditProject = observer(function FormEditProject() {
  const store = useContext(FormProjectCtx);
  const router = useRouter();
  const projectID = getQueryParam("id", router.query);
  const fromProjectId = getQueryParamOptional("from_project_id", router.query);

  const [updateProject, updateProjectState] = useUpdateProjectMutation({
    onCompleted(data) {
      router.push({
        pathname: "/projects/detail",
        query: { id: data.updateProject.id },
      });
    },
    ...onError(),
  });

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = await store.form.validate();

    if (payload === null) return;

    await updateProject({
      variables: {
        input: {
          ...omit(payload, "code", "rate_card_id", "rate_card_exchange_rate"),
          id: projectID,
          from_duplicate_project_id: fromProjectId,
        },
        propagate_billable: store.billabilityChanged
          ? store.form.propagate_billable.$
          : false,
      },
    });
  };

  const cancelModal = useDisclosure();

  return (
    <React.Fragment>
      {updateProjectState.loading && <Spinner absolute />}

      <FormProjectCmp
        onSubmit={onSubmit}
        onCancel={cancelModal.onOpen}
        loading={updateProjectState.loading}
        submitTitle={t`Update project`}
        isEdit
        isTemplate={store.project?.is_template}
      />

      <ModalConfirm {...cancelModal} onConfirm={() => Router.back()}>
        <Trans>Are you sure to cancel editing project?</Trans>
      </ModalConfirm>
    </React.Fragment>
  );
});

function useData(store: StoreProjectForm) {
  const { query } = useRouter();

  const id = getQueryParam("id", query);

  const { loading, data } = useGetProjectEditDataQuery({
    variables: { id },
    skip: !id,
    onCompleted(data) {
      store.fromGetProjectEditDataQuery(data);
    },
    ...onError(),
  });

  return { loading, data };
}

export const PageProjectEdit = withCan(
  { do: ["project_update_own", "project_update_all", "project_update_team"] },
  observer(function PageCreate() {
    const [store] = useState(() => {
      return StoreProjectForm.create();
    });
    const { loading, data } = useData(store);

    if (loading) {
      return <Spinner absolute />;
    }

    if (!data?.project) {
      return <NotFoundProject />;
    }

    return (
      <FormProjectCtx.Provider value={store}>
        <FormEditProject />
      </FormProjectCtx.Provider>
    );
  }),
);
